import { useEffect, useState } from 'react';

/**
 * The drum a split-flap unit carries. Real units step through their drum in
 * order, so a cell reaching "7" from blank rattles through 0-6 on the way
 * rather than jumping: the same here, which is most of why it reads as a board
 * rather than as text fading in.
 */
export const DRUM = " 0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ.,:-+%'/";

export interface FlapCell {
  ch: string;
  /** What the flap is folding away from. */
  prev: string;
  /** Bumps on every change so the fold animation restarts. */
  tick: number;
}

/** The characters a cell shows on its way to `target`, ending on it. Exported for tests. */
export function drumPath(target: string, steps: number): string[] {
  const at = DRUM.indexOf(target.toUpperCase());
  if (at <= 0) return [target]; // blank, or not on the drum: show it straight away
  const n = Math.min(steps, DRUM.length - 1);
  const out: string[] = [];
  for (let k = n - 1; k >= 0; k--) out.push(DRUM[(at - k + DRUM.length) % DRUM.length]!);
  return out;
}

interface Options {
  /** ms before the first cell starts. */
  delay?: number;
  /** ms between a cell's flips. */
  step?: number;
  /** ms between one cell starting and the next. */
  stagger?: number;
  /** Skip the rattle and show the value at once. */
  instant?: boolean;
}

export function useFlap(
  value: string,
  { delay = 0, step = 58, stagger = 70, instant = false }: Options = {},
): FlapCell[] {
  const [cells, setCells] = useState<FlapCell[]>(() =>
    [...value].map((ch) => (instant ? { ch, prev: ch, tick: 0 } : { ch: ' ', prev: ' ', tick: 0 })),
  );

  useEffect(() => {
    const chars = [...value];
    if (instant) {
      setCells(chars.map((ch) => ({ ch, prev: ch, tick: 0 })));
      return;
    }
    setCells(chars.map(() => ({ ch: ' ', prev: ' ', tick: 0 })));
    const timers: number[] = [];
    chars.forEach((target, i) => {
      // Later cells turn more times, so the board settles left to right.
      const path = drumPath(target, 5 + ((i * 3) % 6));
      path.forEach((ch, k) => {
        timers.push(
          window.setTimeout(
            () =>
              setCells((prev) => {
                const next = prev.slice();
                const was = next[i] ?? { ch: ' ', prev: ' ', tick: 0 };
                next[i] = { ch, prev: was.ch, tick: was.tick + 1 };
                return next;
              }),
            delay + i * stagger + k * step,
          ),
        );
      });
    });
    return () => timers.forEach(window.clearTimeout);
  }, [value, delay, step, stagger, instant]);

  return cells;
}
